import { useMemo } from "react";
import { AnalyticalTable, Badge, Button, Icon, type AnalyticalColumn, type BadgeTone } from "@conuti-das/prince-ui";
import { Flex, Stack, princeIcon } from "@/ui-prince";
import type { Ticket, ReviewDecision, ReviewState } from "@/types/tickets";

type Props = {
  tickets: Ticket[];
  getDecision: (t: Ticket) => ReviewDecision | undefined;
  onSelect: (t: Ticket) => void;
  stateToObj: Record<ReviewState, "Positive" | "Critical" | "Negative" | "None" | "Information">;
};

// UI5-ObjectStatus-States → prince Badge-Tones.
const OBJ_TO_TONE: Record<string, BadgeTone> = {
  Positive: "green",
  Critical: "orange",
  Negative: "red",
  Information: "blue",
  None: "gray",
};

const RELEVANCE_TO_OBJ: Record<string, "Negative" | "Critical" | "None" | "Information"> = {
  kritisch: "Negative",
  hoch: "Critical",
  normal: "Information",
  gering: "None",
  unbewertet: "None",
};

// Numerische Sort-Reihenfolge für Relevanz und Status — sorgt für eine fachlich
// sinnvolle Sortierung (kritisch zuerst) statt alphabetisch.
const RELEVANCE_ORDER: Record<string, number> = {
  kritisch: 0, hoch: 1, normal: 2, gering: 3, unbewertet: 4,
};
const STATE_ORDER: Record<string, number> = {
  pending: 0, approved: 1, rejected: 2, "in-jira": 3,
};

type Row = {
  ticket: Ticket;
  aend_id: string;
  doctype: string;
  business_relevance: string;
  title: string;
  matched_components: string[];
  confidence: number;
  review_reasons: string[];
  state: ReviewState;
};

export function ReviewTable({ tickets, getDecision, onSelect, stateToObj }: Props) {
  const data = useMemo<Row[]>(
    () =>
      tickets.map((t) => {
        const titleLine =
          t.body.split("\n").find((l) => l.startsWith("# "))?.slice(2).trim() ?? t.file;
        const decision = getDecision(t);
        return {
          ticket: t,
          aend_id: t.aend_id,
          doctype: t.doctype,
          business_relevance: t.business_relevance ?? "unbewertet",
          title: titleLine,
          matched_components: t.matched_components ?? [],
          confidence: t.confidence ?? 0,
          review_reasons: t.review_reasons ?? [],
          state: (decision?.state ?? "pending") as ReviewState,
        };
      }),
    [tickets, getDecision]
  );

  const columns = useMemo<AnalyticalColumn<Row>[]>(
    () => [
      {
        header: "aend_id",
        accessorKey: "aend_id",
        width: "90px",
        cellRender: (row) => (
          <code style={{ fontSize: "0.8rem" }}>{row.aend_id}</code>
        ),
      },
      {
        header: "Doc",
        accessorKey: "doctype",
        width: "70px",
        cellRender: (row) => (
          <Badge tone="teal">{row.doctype}</Badge>
        ),
      },
      {
        header: "Relevanz",
        // Sortierung über die fachliche Reihenfolge (kritisch zuerst).
        accessor: (row) => RELEVANCE_ORDER[row.business_relevance] ?? 99,
        sortType: "basic",
        width: "120px",
        cellRender: (row) => (
          <Badge tone={OBJ_TO_TONE[RELEVANCE_TO_OBJ[row.business_relevance] ?? "None"]}>
            {row.business_relevance}
          </Badge>
        ),
      },
      {
        header: "Titel",
        accessorKey: "title",
        minWidth: 280,
        cellRender: (row) => (
          <Stack style={{ gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.title}</span>
            {row.review_reasons.length > 0 && (
              <Flex style={{ gap: 4, flexWrap: "wrap" }}>
                {row.review_reasons.slice(0, 3).map((r) => (
                  <Badge key={r} tone="orange">{r}</Badge>
                ))}
              </Flex>
            )}
          </Stack>
        ),
      },
      {
        header: "Komponenten",
        // Sortierung über die zusammengefügten Komponenten-Namen.
        accessor: (row) => row.matched_components.join(","),
        sortType: "alphanumeric",
        minWidth: 220,
        cellRender: (row) => (
          <Flex style={{ gap: 3, flexWrap: "wrap" }}>
            {row.matched_components.slice(0, 4).map((c) => (
              <Badge key={c} tone="neutral">{c}</Badge>
            ))}
            {row.matched_components.length > 4 && (
              <span className="muted" style={{ fontSize: "0.7rem" }}>
                +{row.matched_components.length - 4}
              </span>
            )}
          </Flex>
        ),
      },
      {
        header: "Conf",
        accessorKey: "confidence",
        width: "70px",
        align: "end",
        cellRender: (row) => (
          <span style={{ fontSize: "0.78rem" }}>
            {row.confidence ? row.confidence.toFixed(2) : "—"}
          </span>
        ),
      },
      {
        header: "Status",
        // Sortierung über die fachliche Reihenfolge (pending zuerst).
        accessor: (row) => STATE_ORDER[row.state] ?? 99,
        sortType: "basic",
        width: "120px",
        cellRender: (row) => (
          <Badge tone={OBJ_TO_TONE[stateToObj[row.state]]}>
            {row.state}
          </Badge>
        ),
      },
      {
        header: "Aktion",
        id: "actions",
        width: "110px",
        disableFilters: true,
        disableSortBy: true,
        cellRender: (row) => (
          <Button
            variant="plain"
            onPress={() => onSelect(row.ticket)}
          >
            <Icon name={princeIcon("inspect")} size={16} />
            Review
          </Button>
        ),
      },
    ],
    [onSelect, stateToObj]
  );

  return (
    <AnalyticalTable
      data={data}
      columns={columns}
      visibleRows={20}
      minRows={5}
      sortable
      filterable
      enableGrouping
      scaleWidthMode="smart"
      onRowClick={({ row }) => {
        if (row?.ticket) onSelect(row.ticket);
      }}
      noDataText="Keine Tickets in dieser Filter-Kombination."
      header={`${data.length} Tickets`}
    />
  );
}
